import { Set, Map } from 'immutable';
import * as Immutable from 'immutable';
import { TutorialAction, TutorialActionTypes, TutorialCompletionMap } from 'Types/TutorialTypes';

export interface TutorialReducerState {
  completed: Immutable.Map<number, Immutable.Set<number>>;
  lastChapter: Immutable.Map<number, number>;
  playbackSpeed: number;
  manualPause?: boolean;
}

const defaultState: TutorialReducerState = {
  completed: Map<number, Set<number>>([]),
  lastChapter: Map<number, number>([]),
  playbackSpeed: 1,
  manualPause: undefined,
};

function mergeCompletionMap(
  existing: Immutable.Map<number, Immutable.Set<number>>,
  incoming: TutorialCompletionMap,
): Immutable.Map<number, Immutable.Set<number>> {
  return Object.entries(incoming).reduce((acc, [levelStr, chapters]) => {
    const level = parseInt(levelStr, 10);
    // `acc` may be plain JS rather than Immutable here: redux-persist serializes
    // the completion map to JSON, so after rehydration each level's value comes
    // back as a plain array with no `.union` method. Wrap it in an Immutable.Set
    // first, mirroring MARK_CHAPTER_COMPLETE and the TutorialProgress helpers.
    const existingChapters = Immutable.Set<number>(Immutable.get(acc, level) || []);
    return Immutable.set(acc, level, existingChapters.union(chapters ?? []));
  }, existing);
}

export function tutorialReducer(
  state: TutorialReducerState = defaultState,
  action: TutorialAction,
): TutorialReducerState {
  switch (action.type) {
    case TutorialActionTypes.MARK_CHAPTER_COMPLETE: {
      return {
        ...state,
        completed: Immutable.set(
          state.completed,
          action.payload.lesson,
          Immutable.Set(Immutable.get(state.completed, action.payload.lesson) || []).union([
            action.payload.chapter,
          ]),
        ),
      };
    }
    case TutorialActionTypes.HYDRATE_COMPLETION: {
      return {
        ...state,
        completed: mergeCompletionMap(state.completed, action.payload),
      };
    }
    case TutorialActionTypes.SET_LAST_CHAPTER: {
      return {
        ...state,
        lastChapter: Immutable.set(
          state.lastChapter,
          action.payload.lesson,
          action.payload.chapter,
        ),
      };
    }
    case TutorialActionTypes.SET_PLAYBACK_SPEED: {
      return {
        ...state,
        playbackSpeed: action.payload ?? state.playbackSpeed,
      };
    }
    case TutorialActionTypes.SET_MANUAL_PAUSE: {
      return {
        ...state,
        manualPause: action.payload.manualPause,
      };
    }
    default:
      return state;
  }
}
